import {TimeAccountBaseAccount, TimeAccountOvertime, TimeAccountTravellingTime} from "@/shared/timr";
import {formatPercent} from "@/shared/utils/misc";
import {formatDuration} from "@/shared/utils/datefunctions";
import {TimeAccountEntryType} from "@/shared/timrTypes";

/**
 * Renders a detailed list/table of overtimes (TimeAccountOvertime) in a popover
 */
export function renderOvertimesPopoverContent(overtimes: TimeAccountOvertime[], allowancesOnly: boolean, timeAccountEntryType?: TimeAccountEntryType): JQuery {
	if (timeAccountEntryType === TimeAccountEntryType.OvertimeCorrection) {
		return renderOvertimesPopoverContentInternal(overtimes, allowancesOnly, false, false, true, false, false, false, false);
	} else if (timeAccountEntryType === TimeAccountEntryType.OvertimeTimeAccountTransfer) {
		return renderOvertimesPopoverContentInternal(overtimes, allowancesOnly, false, false, false, true, false, false, false);
	} else if (timeAccountEntryType === TimeAccountEntryType.OverTimePayoff || timeAccountEntryType === TimeAccountEntryType.ExitPayoff) {
		return renderOvertimesPopoverContentInternal(overtimes, allowancesOnly, false, false, false, false, true, false, false);
	}
	return renderOvertimesPopoverContentInternal(overtimes, allowancesOnly);
}

/**
 * Renders a detailed list/table of overtimes (TimeAccountOvertime) in a popover
 */
function renderOvertimesPopoverContentInternal(overtimes: TimeAccountOvertime[], allowancesOnly: boolean, showLastDurationTotal = true,
											   showDuration = true, showCorrections = true, showTransfers = true, showPayoffs = true, showTotalDuration = true, showTotal = true): JQuery {
	if (!overtimes?.length) {
		return null;
	}
	const content = $("<div>");
	if (overtimes?.length) {
		if (allowancesOnly) {
			content.append(buildTimeAccountOvertimeAllowanceTable(overtimes, messages["timeAccount.overtimes.tooltip.allowances"],
				messages["timeAccount.overtimes.tooltip.allowances.short"],
				(entry) => {
					const overtimeRule = (entry as TimeAccountOvertime).overtimeRule;
					return overtimeRule.shortName + ": " + overtimeRule.name;
				},
				showLastDurationTotal, showDuration, showCorrections, showTransfers, showPayoffs, showTotalDuration, false, false, true));
		} else {
			content.append(buildTimeAccountOvertimeAllowanceTable(overtimes, messages["timeAccount.overtimes.tooltip.overtimes"],
				messages["timeAccount.overtimes.tooltip.overtimes.short"],
				(entry) => {
					const overtimeRule = (entry as TimeAccountOvertime).overtimeRule;
					return overtimeRule.shortName + ": " + overtimeRule.name;
				},
				showLastDurationTotal, showDuration, showCorrections, showTransfers, showPayoffs, showTotalDuration, showTotal));
		}
	}

	return content;
}

export function renderTravellingTimesPopoverContent(travellingTimes: TimeAccountTravellingTime[], timeAccountEntryType?: TimeAccountEntryType): JQuery {
	if (timeAccountEntryType === TimeAccountEntryType.TravellingTimeCorrection) {
		return renderTravellingTimesPopoverContentInternal(travellingTimes, false, false, true, false, false, false);
	} else if (timeAccountEntryType === TimeAccountEntryType.TravellingTimeTransfer) {
		return renderTravellingTimesPopoverContentInternal(travellingTimes, false, false, false, true, false, false);
	} else if (timeAccountEntryType === TimeAccountEntryType.TravellingTimePayoff || timeAccountEntryType === TimeAccountEntryType.ExitPayoff) {
		return renderTravellingTimesPopoverContentInternal(travellingTimes, false, false, false, false, true, false);
	}
	return renderTravellingTimesPopoverContentInternal(travellingTimes)
}

function renderTravellingTimesPopoverContentInternal(travellingTimes: TimeAccountTravellingTime[], showLastDurationTotal = true,
													 showDuration = true, showCorrections = true, showTransfers = true,
													 showPayoffs = true, showTotalDuration = true): JQuery {
	if (!travellingTimes?.length) {
		return null;
	}
	const content = $("<div>");
	if (travellingTimes?.length) {
		content.append(buildTimeAccountOvertimeAllowanceTable(travellingTimes, messages["timeAccount.travellingTimes.tooltip.travellingTimes"],
			messages["timeAccount.travellingTimes.tooltip.travellingTimes.short"],
			(entry) => {
				const timeAccountTravellingTime = entry as TimeAccountTravellingTime;
				return messages["workingTimeTypeSubCategory." + timeAccountTravellingTime.subCategory]; // DEVNOTE: maybe add percentage?
			},
			showLastDurationTotal, showDuration, showCorrections, showTransfers, showPayoffs, showTotalDuration, false, true, false));
	}
	return content;
}

export function buildTimeAccountOvertimeAllowanceTable(entries: TimeAccountBaseAccount[], titleLabel: string, titleShort: string, titleCallback: (entry: TimeAccountBaseAccount) => string,
													   showLastDurationTotal: boolean, showDuration: boolean, showCorrections: boolean, showTransfers: boolean, showPayoffs: boolean, showTotalDuration: boolean,
													   showTotal: boolean, showOvertimeColumns = true, showAllowanceColumns = true): JQuery {
	const table = $("<table>").addClass("overtimes-table");
	if (showOvertimeColumns !== showAllowanceColumns) {
		table.addClass("single-visible-column");
	}
	const trHeader = $("<tr>");
	const trSubHeader = $("<tr>").addClass("border-bottom");
	const getVisibleColumnCount = (overtimeColumns: number, allowanceColumns: number) =>
		(showOvertimeColumns ? overtimeColumns : 0) + (showAllowanceColumns ? allowanceColumns : 0);

	trHeader.append($("<th>").text(titleLabel).attr("rowspan", 2));
	trSubHeader.append($("<th>").css("display", "none")); // required for correct css selection first-child because of rowspan
	trHeader.append($("<th>").attr("rowspan", 2));
	trSubHeader.append($("<th>").css("display", "none"));
	if (showLastDurationTotal) {
		const visibleColumnCount = getVisibleColumnCount(1, 1);
		if (visibleColumnCount > 0) {
			trHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.lastPeriod"]).attr("colspan", visibleColumnCount).addClass("border-left text-center"));
			if (showOvertimeColumns) {
				trSubHeader.append($("<th>").text(titleShort).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				trSubHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.allowances.short"]).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
	}
	if (showDuration) {
		const visibleColumnCount = getVisibleColumnCount(1, 2);
		if (visibleColumnCount > 0) {
			trHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.running"]).attr("colspan", visibleColumnCount).addClass("border-left text-center"));
			if (showOvertimeColumns) {
				trSubHeader.append($("<th>").text(titleShort).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				trSubHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.allowancesForDuration.short"]).addClass(showOvertimeColumns ? "" : "border-left"))
					.append($("<th>").text(messages["timeAccount.overtimes.tooltip.allowances.short"]));
			}
		}
	}
	if (showCorrections) {
		const visibleColumnCount = getVisibleColumnCount(1, 1);
		if (visibleColumnCount > 0) {
			trHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.corrections"]).attr("colspan", visibleColumnCount).addClass("border-left text-center"));
			if (showOvertimeColumns) {
				trSubHeader.append($("<th>").text(titleShort).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				trSubHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.allowances.short"]).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
	}
	if (showTransfers) {
		const visibleColumnCount = getVisibleColumnCount(1, 1);
		if (visibleColumnCount > 0) {
			trHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.transfers"]).attr("colspan", visibleColumnCount).addClass("border-left text-center"));
			if (showOvertimeColumns) {
				trSubHeader.append($("<th>").text(titleShort).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				trSubHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.allowances.short"]).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
	}
	if (showPayoffs) {
		const visibleColumnCount = getVisibleColumnCount(1, 1);
		if (visibleColumnCount > 0) {
			trHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.payoffs"]).attr("colspan", visibleColumnCount).addClass("border-left text-center"));
			if (showOvertimeColumns) {
				trSubHeader.append($("<th>").text(titleShort).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				trSubHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.allowances.short"]).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
	}
	if (showTotalDuration) {
		const visibleColumnCount = getVisibleColumnCount(1, 1);
		if (visibleColumnCount > 0) {
			trHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.account"]).attr("colspan", visibleColumnCount).addClass("border-left text-center"));
			if (showOvertimeColumns) {
				trSubHeader.append($("<th>").text(titleShort).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				trSubHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.allowances.short"]).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
	}
	if (showTotal) {
		trHeader.append($("<th>").text(messages["timeAccount.overtimes.tooltip.total"]).attr("rowspan", 2).addClass("border-left"));
	}
	table.append(trHeader);
	table.append(trSubHeader);
	let lastOvertimeDurationTotalSum = 0;
	let lastAllowanceDurationTotalSum = 0;
	let overtimeDurationSum = 0;
	let allowanceForDurationSum = 0;
	let allowanceDurationSum = 0;
	let overtimeCorrectionsSum = 0;
	let overtimeTransfersSum = 0;
	let overtimePayoffsSum = 0;
	let allowanceCorrectionsSum = 0;
	let allowanceTransfersSum = 0;
	let allowancePayoffsSum = 0;
	let overtimeDurationTotalSum = 0;
	let allowanceDurationTotalSum = 0;
	let totalDurationSum = 0;
	for (const entry of entries) {
		lastOvertimeDurationTotalSum += entry.lastOvertimeDurationTotal || 0;
		lastAllowanceDurationTotalSum += entry.lastAllowanceDurationTotal || 0;
		overtimeDurationSum += entry.overtimeDuration || 0;
		allowanceForDurationSum += entry.allowanceForDuration || 0;
		allowanceDurationSum += entry.allowanceDuration || 0;
		overtimeCorrectionsSum += entry.overtimeCorrections || 0;
		overtimeTransfersSum += entry.overtimeTransfers || 0;
		overtimePayoffsSum += entry.overtimePayoffs || 0;
		allowanceCorrectionsSum += entry.allowanceCorrections || 0;
		allowanceTransfersSum += entry.allowanceTransfers || 0;
		allowancePayoffsSum += entry.allowancePayoffs || 0;
		overtimeDurationTotalSum += entry.overtimeDurationTotal || 0;
		allowanceDurationTotalSum += entry.allowanceDurationTotal || 0;
		totalDurationSum += entry.totalDuration || 0;
		const tr = $("<tr>");
		tr.append($("<td>").addClass("overtimes-table-title")
			.text(titleCallback(entry)));
		tr.append($("<td>").text(formatPercent(entry.percentage, 0)));
		if (showLastDurationTotal) {
			if (showOvertimeColumns) {
				tr.append($("<td>").text(formatDuration(entry.lastOvertimeDurationTotal)).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				tr.append($("<td>").text(formatDuration(entry.lastAllowanceDurationTotal)).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
		if (showDuration) {
			if (showOvertimeColumns) {
				tr.append($("<td>").text(formatDuration(entry.overtimeDuration)).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				tr.append($("<td>").text(formatDuration(entry.allowanceForDuration)).addClass(showOvertimeColumns ? "" : "border-left"))
					.append($("<td>").text(formatDuration(entry.allowanceDuration)));
			}
		}
		if (showCorrections) {
			if (showOvertimeColumns) {
				tr.append($("<td>").text(formatDuration(entry.overtimeCorrections)).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				tr.append($("<td>").text(formatDuration(entry.allowanceCorrections)).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
		if (showTransfers) {
			if (showOvertimeColumns) {
				tr.append($("<td>").text(formatDuration(entry.overtimeTransfers)).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				tr.append($("<td>").text(formatDuration(entry.allowanceTransfers)).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
		if (showPayoffs) {
			if (showOvertimeColumns) {
				tr.append($("<td>").text(formatDuration(entry.overtimePayoffs)).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				tr.append($("<td>").text(formatDuration(entry.allowancePayoffs)).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
		if (showTotalDuration) {
			if (showOvertimeColumns) {
				tr.append($("<td>").text(formatDuration(entry.overtimeDurationTotal)).addClass("border-left"));
			}
			if (showAllowanceColumns) {
				tr.append($("<td>").text(formatDuration(entry.allowanceDurationTotal)).addClass(showOvertimeColumns ? "" : "border-left"));
			}
		}
		if (showTotal) {
			tr.append($("<td>").text(formatDuration(entry.totalDuration)).addClass("border-left"));
		}
		table.append(tr);
	}
	const trFooter = $("<tr>").addClass("text-semibold").addClass("border-top");
	trFooter.append($("<td>").text(messages["timeAccount.overtimes.tooltip.sum"]));
	trFooter.append($("<td>"));
	if (showLastDurationTotal) {
		if (showOvertimeColumns) {
			trFooter.append($("<td>").text(formatDuration(lastOvertimeDurationTotalSum)).addClass("border-left"));
		}
		if (showAllowanceColumns) {
			trFooter.append($("<td>").text(formatDuration(lastAllowanceDurationTotalSum)).addClass(showOvertimeColumns ? "" : "border-left"));
		}
	}
	if (showDuration) {
		if (showOvertimeColumns) {
			trFooter.append($("<td>").text(formatDuration(overtimeDurationSum)).addClass("border-left"));
		}
		if (showAllowanceColumns) {
			trFooter.append($("<td>").text(formatDuration(allowanceForDurationSum)).addClass(showOvertimeColumns ? "" : "border-left"))
				.append($("<td>").text(formatDuration(allowanceDurationSum)));
		}
	}
	if (showCorrections) {
		if (showOvertimeColumns) {
			trFooter.append($("<td>").text(formatDuration(overtimeCorrectionsSum)).addClass("border-left"));
		}
		if (showAllowanceColumns) {
			trFooter.append($("<td>").text(formatDuration(allowanceCorrectionsSum)).addClass(showOvertimeColumns ? "" : "border-left"));
		}
	}
	if (showTransfers) {
		if (showOvertimeColumns) {
			trFooter.append($("<td>").text(formatDuration(overtimeTransfersSum)).addClass("border-left"));
		}
		if (showAllowanceColumns) {
			trFooter.append($("<td>").text(formatDuration(allowanceTransfersSum)).addClass(showOvertimeColumns ? "" : "border-left"));
		}
	}
	if (showPayoffs) {
		if (showOvertimeColumns) {
			trFooter.append($("<td>").text(formatDuration(overtimePayoffsSum)).addClass("border-left"));
		}
		if (showAllowanceColumns) {
			trFooter.append($("<td>").text(formatDuration(allowancePayoffsSum)).addClass(showOvertimeColumns ? "" : "border-left"));
		}
	}
	if (showTotalDuration) {
		if (showOvertimeColumns) {
			trFooter.append($("<td>").text(formatDuration(overtimeDurationTotalSum)).addClass("border-left"));
		}
		if (showAllowanceColumns) {
			trFooter.append($("<td>").text(formatDuration(allowanceDurationTotalSum)).addClass(showOvertimeColumns ? "" : "border-left"));
		}
	}
	if (showTotal) {
		trFooter.append($("<td>").text(formatDuration(totalDurationSum)).addClass("border-left"));
	}
	table.append(trFooter);
	return table;
}
